import { datadogLogs } from '@datadog/browser-logs';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { selectors as companySelectors } from 'src/features/company/companySlice';
import useCompany from 'src/features/company/hooks/useCompany';
import { useEntryById } from 'src/features/entries/hooks';
import { useFrameworkUI } from 'src/features/frameworkUI/hooks';
import { selectors as myWorkSelectors } from 'src/features/myWork';
import { useProofsFilteredByEntryId, useZeroProofsLogging } from 'src/features/proofs/hooks';
import { getProofsByEntryIdThunk, updateProofThunk } from 'src/features/proofs/services';
import useProperty from 'src/features/property/hooks/useProperty';
import { selectors as propertySelectors } from 'src/features/property/propertySlice';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { useScript } from 'src/hooks/useScript';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { useAppDispatch } from 'src/store';
import { FeatureFlagKey } from 'src/ts/enums/featureFlag';

import { useFolderById } from 'src/features/folders/hooks';
import { useMatchingEntriesByEntryId } from 'src/features/matchingEntries/hooks';
import { IEntry, IMatchingEntry, IProof } from 'src/ts/interfaces';
import { useFolderPage } from '../FolderPageContext';
import { ReportPlatformFeedbackModalContainer, SplitEntryModal } from '../modals';
import SendReportFraudModal from '../modals/SendReportFraudModal';
import {
  DocumentViewer,
  InspectorPanel,
  LeftPanelDetails,
  OfferLetterVerificationPanel,
  SubmissionReviewPanel
} from './components';
import PotentialMatchesScreen from './components/PotentialMatches/PotentialMatchesScreen';
import { FraudTrendEvidenceProvider } from './hooks/useFraudTrendEvidence';
import {
  buildProofUpdateData,
  FormValues,
  getEffectiveProofResult,
  getProofData,
  hasAllRequiredFields,
  isAutomaticRulingOverridden,
  needsAnalystRuling,
  isEntryRuled,
  isPropertyOlvEnabled,
  isReclassificationStatus,
  isUnverifiedOfferLetter,
  OFFER_LETTER_TYPE
} from './helpers/proofHelpers';
import { useDocumentNavigation } from './hooks';
import { useBlockedEditedAutoRule } from './hooks/useBlockedEditedAutoRule';
import { useOlvLockState } from './hooks/useOlvLockState';
import { Container } from './styles';

interface IProps {
  entries?: IEntry[];
}

const ModernizedFraudAnalystReview: FC<IProps> = ({ entries }) => {
  useScript('https://documentservices.adobe.com/view-sdk/viewer.js');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { folderId } = useParams();
  const { isOpenSidebar, onSetIsOpenSidebar } = useFrameworkUI();
  const assignedEntry = useSelector(myWorkSelectors.assignedEntry);
  const { setShowSendReportModal, onChangeSelectedEntry } = useFolderPage();
  const { showSnackbar, VariantType } = useSnackbarProvider();

  const [selectedEntryId, setSelectedEntryId] = useState<string>(assignedEntry?.id || '');
  const [isSubmissionReviewMode, setIsSubmissionReviewMode] = useState<boolean>(false);
  const { entry } = useEntryById(selectedEntryId);
  const [matchingEntriesScreenDismissed, setMatchingEntriesScreenDismissed] =
    useState<boolean>(false);
  const [userDismissedMatches, setUserDismissedMatches] = useState<boolean>(false);

  const { onGetMatchingEntriesByEntryId, matches } = useMatchingEntriesByEntryId(
    selectedEntryId as string
  );

  const hasMatches = matches.length > 0;

  const isInspectorPanelEnabled = useFeatureFlag(FeatureFlagKey.ITL_2960_INSPECTOR_PANEL);
  const isReportPlatformFeedbackEnabled = useFeatureFlag(
    FeatureFlagKey.ITL_3152_REPORT_PLATFORM_FEEDBACK
  );
  const [showReportPlatformFeedbackModal, setShowReportPlatformFeedbackModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (selectedEntryId) {
      onGetMatchingEntriesByEntryId(selectedEntryId);
    }
  }, [selectedEntryId, onGetMatchingEntriesByEntryId]);

  useEffect(() => {
    if (selectedEntryId === assignedEntry?.id && !userDismissedMatches) {
      setMatchingEntriesScreenDismissed(false);
    } else {
      setMatchingEntriesScreenDismissed(true);
    }
  }, [selectedEntryId, userDismissedMatches, assignedEntry?.id]);
  const { folder } = useFolderById(folderId || '');

  const {
    proofs,
    isLoading: isLoadingProofs,
    isSuccess: isProofsSuccess
  } = useProofsFilteredByEntryId(selectedEntryId);

  // ITL-2772: tracks which entries have already triggered the auto-flip to
  // SubmissionReviewMode on mount. Prevents the effect below from yanking
  // the user back if they manually navigate to a hypothetical carousel view
  // for an entry that's already been auto-flipped. Resets on entry navigation
  // so a re-entered entry that acquired a new manually-reviewable proof
  // (e.g. from useBlockedEditedAutoRule's cascade refetch) gets re-evaluated
  // — mirrors the per-navigation reset pattern in useBlockedEditedAutoRule.
  const autoFlippedEntries = useRef<Set<string>>(new Set());

  useEffect(() => {
    autoFlippedEntries.current.clear();
  }, [selectedEntryId]);

  // ITL-2772: when the loaded entry has no manually-reviewable proofs (all
  // of them are either AV-ruled or auto-insufficient UNVERIFIED_OFFER_LETTER),
  // the carousel has nothing actionable to show. On a page refresh that lands
  // on such an entry, drop straight into SubmissionReviewPanel instead of
  // showing a terminal OL doc in the carousel with no actions and a still-
  // visible (but non-functional) document-type dropdown.
  useEffect(() => {
    if (isLoadingProofs) return;
    if (!selectedEntryId) return;
    if (autoFlippedEntries.current.has(selectedEntryId)) return;
    if (proofs.length === 0) return;

    const hasManuallyReviewable = proofs.some(
      (p) => !p.is_automatic_review && !isUnverifiedOfferLetter(p)
    );

    if (!hasManuallyReviewable) {
      autoFlippedEntries.current.add(selectedEntryId);
      setIsSubmissionReviewMode(true);
    }
  }, [proofs, selectedEntryId, isLoadingProofs]);

  const { onGetById: onGetProperty } = useProperty();
  const getPropertyData = useSelector(propertySelectors.getOne.data);
  // ITL-2772: drives the reclassify-to-OL pre-flight modal copy. The BE's
  // property gate is the authoritative check; this is the FE counterpart
  // that picks accurate language ("auto-Insufficient" vs Greystar "queue +
  // AI pipeline") so the analyst isn't misled before they click Continue.
  const propertyOlvEnabled = isPropertyOlvEnabled(getPropertyData);

  const { onGetOneById: onGetCompany } = useCompany();
  const getCompanyData = useSelector(companySelectors.getOne.data);
  const getCompanyIsIdle = useSelector(companySelectors.getOne.isIdle);

  const {
    selectedProofId,
    setSelectedProofId,
    currentProof,
    manuallyReviewableProofs,
    currentManualIndex,
    manualProofCount,
    isFirstDocument,
    isLastDocument,
    canNavigateBack,
    canNavigateForward,
    handleNextDocument,
    handlePreviousDocument
  } = useDocumentNavigation({ proofs });

  const avRuledProofs = useMemo(
    () => proofs.filter((p) => p.is_automatic_review && !isUnverifiedOfferLetter(p)),
    [proofs]
  );

  const isPotentialMatchesVisible = !matchingEntriesScreenDismissed && hasMatches;

  const isDocumentViewerVisible = !isPotentialMatchesVisible && !isSubmissionReviewMode;

  const [localFormData, setLocalFormData] = useState<Record<string, FormValues>>({});

  const effectiveProofType = currentProof
    ? localFormData[currentProof.id]?.documentType || (currentProof.type as string)
    : undefined;
  // ITL-2772: when a non-Greystar reclassification auto-resolves the proof
  // to :UNDETERMINED + UNVERIFIED_OFFER_LETTER, the OLV panel must NOT
  // re-mount on that proof — its action buttons would expose the same
  // leak window the controller 403 guard already blocks. Hiding the
  // panel keeps the FE consistent with the proof's terminal state.
  const isCurrentProofAutoInsufficient = currentProof
    ? isUnverifiedOfferLetter(currentProof)
    : false;
  const showOfferLetterPanel =
    effectiveProofType === OFFER_LETTER_TYPE &&
    isDocumentViewerVisible &&
    !isCurrentProofAutoInsufficient;

  const olvLockState = useOlvLockState({ proofs, localFormData, currentProof });

  const [entryStatuses, setEntryStatuses] = useState<
    Record<string, 'PENDING' | 'CLEAN' | 'EDITED' | 'UNDETERMINED'>
  >({});
  const [entryProofCache, setEntryProofCache] = useState<Record<string, IProof[]>>({});

  // ITL-2772: the singleton property store stays sticky after first SUCCESS,
  // so the original `isIdle && property_id` gate skipped the refetch when
  // the analyst navigated to an entry on a different property. That left
  // `property_features` pointing at the previous property and caused the
  // reclassify pre-flight modal to pick the wrong copy. Refetch whenever
  // the cached property's id doesn't match the current folder's.
  //
  // Per-component-mount memo of the last property_id we dispatched for so
  // a failed fetch (network / 404) doesn't infinite-loop — the id-mismatch
  // would otherwise stay true forever and re-dispatch on every render.
  // Allows a different property_id to attempt fresh; same property is
  // only retried by remounting (page refresh / route change).
  const lastAttemptedPropertyId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      folder?.property_id &&
      getPropertyData?.id !== folder.property_id &&
      lastAttemptedPropertyId.current !== folder.property_id
    ) {
      lastAttemptedPropertyId.current = folder.property_id;
      onGetProperty(folder.property_id);
    }
  }, [folder?.property_id, onGetProperty, getPropertyData?.id]);

  useEffect(() => {
    if (getCompanyIsIdle && folder?.company_id) {
      onGetCompany(folder.company_id);
    }
  }, [folder?.company_id, onGetCompany, getCompanyIsIdle]);

  // Force close sidebar on component mount to make room for the review interface
  useEffect(() => {
    if (isOpenSidebar) {
      onSetIsOpenSidebar();
    }
  }, []);

  // Sync selectedEntryId with the FolderPageContext to ensure Remove from Folder uses correct entry
  useEffect(() => {
    if (selectedEntryId && onChangeSelectedEntry) {
      onChangeSelectedEntry(selectedEntryId);
    }
  }, [selectedEntryId, onChangeSelectedEntry]);

  // ITL-2772: reclassification has two paths, decided by the property's
  // `offer_letter_verification` feature state. The analyst already
  // confirmed via the property-aware pre-flight modal in DocumentControls
  // before this fires — here we just steer the post-call action based on
  // the BE's `reclassification_status` discriminator.
  //
  //   "requeued"          → Greystar. Entry returned to queue, AI
  //                         pipeline enqueued. Navigate to /my-work.
  //   "auto_insufficient" → Non-Greystar. Proof was atomically marked
  //                         INSUFFICIENT by the BE. Refetch proofs and
  //                         advance the carousel (or jump to final
  //                         review if this was the last manual proof).
  //
  // The discriminator lives INSIDE the proof body (`response.data`) so
  // the SDK's `ProofResponse = { data?: Proof }` propagates it; emitting
  // at the response root would be stripped.
  const handleReclassifyToOfferLetter = useCallback(
    async (proofId: string) => {
      try {
        const response = await dispatch(
          updateProofThunk({ id: proofId, type: OFFER_LETTER_TYPE as IProof['type'] })
        ).unwrap();

        const proofFromResponse = response?.data as
          | (IProof & { reclassification_status?: string })
          | undefined;
        const reclassificationStatus = proofFromResponse?.reclassification_status;

        // ITL-2772: narrow the BE's discriminator to a known union BEFORE
        // branching. `undefined` (no type change happened), typos, or a
        // future-added third status all fall through here — without this
        // guard the old `else` branch sent every non-"auto_insufficient"
        // value (including undefined) to `/my-work`, which was wrong for
        // the "no type change" path that the BE returns for non-reroute
        // updates.
        if (!isReclassificationStatus(reclassificationStatus)) {
          return;
        }

        if (reclassificationStatus === 'auto_insufficient' && proofFromResponse) {
          // ITL-2772: BE has committed the terminal :UNDETERMINED + [:UNVERIFIED_OFFER_LETTER]
          // state and re-fetched it into the response body (proof_controller's
          // maybe_reload_proof/3). updateProofThunk.fulfilled has already
          // upserted that fresh proof into Redux. Mirror the same confirmed
          // shape into localFormData so display helpers (which read
          // savedData ?? proof.result first) show INSUFFICIENT instead of the
          // stale PENDING the form's pre-PATCH auto-save left there. Using
          // getProofData keeps the BE response as the single source of truth
          // — no hardcoded post-state constants on the FE.
          setLocalFormData((prev) => ({
            ...prev,
            [proofId]: getProofData(proofFromResponse, undefined)
          }));

          // ITL-2772: significant fraud-determination state transition — emit
          // info-level structured log. Datadog Browser SDK auto-correlates to
          // the active RUM trace, so this lands in the same trace as the BE
          // PATCH/Multi span chain.
          datadogLogs.logger.info('Proof reclassified to offer letter', {
            event: 'olv.reclassify.auto_insufficient',
            proof_id: proofId,
            entry_id: selectedEntryId,
            folder_id: folderId,
            company_id: folder?.company_id,
            property_id: folder?.property_id,
            reclassification_status: reclassificationStatus
          });

          // Match the auto-route predicate at :115 — exclude both
          // is_automatic_review proofs and any other already-auto-insufficient
          // OL proofs so a second consecutive reclassification on the same
          // entry doesn't leave the analyst stranded in the carousel.
          const remainingProofs = proofs.filter(
            (p) => !p.is_automatic_review && !isUnverifiedOfferLetter(p) && p.id !== proofId
          );
          const allRemainingRuled =
            remainingProofs.length === 0 ||
            remainingProofs.every((p) => p.result && p.result !== 'PENDING');

          if (allRemainingRuled) {
            setIsSubmissionReviewMode(true);
          } else {
            handleNextDocument();
          }
        } else {
          // ITL-2772: Greystar (requeued) path — entry returned to queue and
          // AI pipeline enqueued. Emit structured log for parity with the
          // auto_insufficient path.
          datadogLogs.logger.info('Proof reclassified to offer letter', {
            event: 'olv.reclassify.requeued',
            proof_id: proofId,
            entry_id: selectedEntryId,
            folder_id: folderId,
            company_id: folder?.company_id,
            property_id: folder?.property_id,
            reclassification_status: reclassificationStatus
          });
          navigate('/my-work');
        }
      } catch (error) {
        datadogLogs.logger.error('Error reclassifying proof to offer letter', {
          error,
          proof_id: proofId,
          entry_id: selectedEntryId,
          folder_id: folderId,
          company_id: folder?.company_id,
          property_id: folder?.property_id
        });
        showSnackbar(
          VariantType.error,
          'Failed to reclassify document to Offer Letter. Please try again.'
        );
      }
    },
    [
      dispatch,
      navigate,
      selectedEntryId,
      folderId,
      folder?.company_id,
      folder?.property_id,
      proofs,
      handleNextDocument,
      showSnackbar,
      VariantType
    ]
  );

  // ITL-2772: FROM-OL changes must also commit synchronously to the BE.
  // Pre-ITL-2772 (ITL-2578), this path only wrote to local form state
  // and deferred the BE write to Send Report's bulk update. That left the
  // proof's DB type as OFFER_LETTER while the FE displayed the new type.
  // If the analyst then changed back to OFFER_LETTER, the resulting PATCH
  // was a same-type write — `maybe_handle_type_change/2` short-circuited
  // and the reclassification module never ran, sending the analyst to
  // /my-work with no auto-insufficient discriminator. Committing FROM-OL
  // here keeps the DB and FE state aligned and fires the reclassification
  // module's `handle_reclassify_from_offer_letter` branch (specializations
  // update on the review item).
  const handleReclassifyFromOfferLetter = useCallback(
    async (proofId: string, newType: IProof['type']) => {
      try {
        const response = await dispatch(updateProofThunk({ id: proofId, type: newType })).unwrap();
        const proofFromResponse = response?.data as
          | (IProof & { reclassification_status?: string })
          | undefined;

        if (proofFromResponse?.reclassification_status === 'requeued') {
          datadogLogs.logger.info('Proof reclassified; list checks re-running', {
            event: 'list_checks.reclassify.requeued',
            proof_id: proofId,
            entry_id: selectedEntryId,
            folder_id: folderId,
            company_id: folder?.company_id,
            property_id: folder?.property_id,
            new_type: newType
          });
          navigate('/my-work');
          return;
        }

        await dispatch(getProofsByEntryIdThunk(selectedEntryId))
          .unwrap()
          .catch(() => null);
      } catch (error) {
        datadogLogs.logger.error('Error reclassifying proof from offer letter', {
          error,
          proof_id: proofId,
          new_type: newType,
          entry_id: selectedEntryId,
          folder_id: folderId,
          company_id: folder?.company_id,
          property_id: folder?.property_id
        });
        showSnackbar(VariantType.error, 'Failed to update document type. Please try again.');
      }
    },
    [
      dispatch,
      navigate,
      selectedEntryId,
      folderId,
      folder?.company_id,
      folder?.property_id,
      showSnackbar,
      VariantType
    ]
  );

  const handleReclassifyCheckSetChange = useCallback(
    async (proofId: string, newType: IProof['type']): Promise<boolean> => {
      try {
        const response = await dispatch(updateProofThunk({ id: proofId, type: newType })).unwrap();
        const proofFromResponse = response?.data as
          | (IProof & { reclassification_status?: string })
          | undefined;

        if (proofFromResponse?.reclassification_status !== 'requeued') return true;

        datadogLogs.logger.info('Proof reclassified; list checks re-running', {
          event: 'list_checks.reclassify.requeued',
          proof_id: proofId,
          entry_id: selectedEntryId,
          folder_id: folderId,
          company_id: folder?.company_id,
          property_id: folder?.property_id,
          new_type: newType
        });
        navigate('/my-work');
        return false;
      } catch (error) {
        datadogLogs.logger.error('Error reclassifying proof', {
          error,
          proof_id: proofId,
          new_type: newType,
          entry_id: selectedEntryId,
          folder_id: folderId,
          company_id: folder?.company_id,
          property_id: folder?.property_id
        });
        showSnackbar(VariantType.error, 'Failed to update document type. Please try again.');
        return false;
      }
    },
    [
      dispatch,
      navigate,
      selectedEntryId,
      folderId,
      folder?.company_id,
      folder?.property_id,
      showSnackbar,
      VariantType
    ]
  );

  const handleReviewReport = () => {
    setIsSubmissionReviewMode(true);
  };

  const handleEditDocument = (entryId: string, proofId: string) => {
    setSelectedEntryId(entryId);
    setSelectedProofId(proofId);
    setIsSubmissionReviewMode(false);
  };

  const updateAllProofsWithFormData = (): Promise<any>[] => {
    const updatePromises: Promise<any>[] = [];

    for (const entry of entries || []) {
      const entryProofs = proofs.filter((p) => p.entry_id === entry.id);

      entryProofs.forEach((proof) => {
        if (entry.id === selectedEntryId) {
          const savedData = localFormData[proof.id];
          const isUnchangedAutomaticRuling =
            proof.is_automatic_review && !isAutomaticRulingOverridden(proof, savedData);
          if (savedData && !isUnchangedAutomaticRuling) {
            updatePromises.push(dispatch(updateProofThunk(buildProofUpdateData(proof, savedData))));
          }
        }
      });
    }

    return updatePromises;
  };

  const handleSendReport = async () => {
    try {
      const updatePromises = updateAllProofsWithFormData();
      await Promise.all(updatePromises);
      setShowSendReportModal?.(true);
    } catch (error) {
      datadogLogs.logger.error('Error updating proofs before send report', {
        error,
        entry_id: selectedEntryId,
        folder_id: folderId,
        company_id: folder?.company_id,
        property_id: folder?.property_id
      });
    }
  };

  const canReportPlatformFeedback = isReportPlatformFeedbackEnabled && !!assignedEntry?.id;

  const handleConfirmSendReport = () => {
    navigate('/my-work');
  };

  const calculateEntryStatus = useCallback(
    (entryId: string, entryProofs: IProof[]) => {
      // ITL-2681 Phase 4 step 5: see checkAssignedEntryRuled below. Same fix:
      // OL proofs gate on proof.result like every other proof type. AV proofs
      // count only once the analyst overrides them.
      const manuallyReviewableProofs = entryProofs.filter((proof) =>
        needsAnalystRuling(proof, localFormData[proof.id])
      );

      if (manuallyReviewableProofs.length === 0) {
        return 'CLEAN';
      }

      const allHaveRequiredFields = manuallyReviewableProofs.every((proof) => {
        const savedData = localFormData[proof.id];
        const { result, reason, documentType, olvReasons } = getProofData(proof, savedData);
        return hasAllRequiredFields(result, documentType, reason, olvReasons);
      });

      if (!allHaveRequiredFields) {
        return 'PENDING';
      }

      const hasUndetermined = manuallyReviewableProofs.some((proof) => {
        const result = getEffectiveProofResult(proof, localFormData[proof.id]);
        return result === 'UNDETERMINED';
      });

      if (hasUndetermined) {
        return 'UNDETERMINED';
      }

      const hasEdited = manuallyReviewableProofs.some((proof) => {
        const result = getEffectiveProofResult(proof, localFormData[proof.id]);
        return result === 'EDITED';
      });

      return hasEdited ? 'EDITED' : 'CLEAN';
    },
    [localFormData]
  );

  useEffect(() => {
    if (selectedEntryId && proofs.length > 0) {
      setEntryProofCache((prev) => ({ ...prev, [selectedEntryId]: proofs }));

      const status = calculateEntryStatus(selectedEntryId, proofs) as
        | 'PENDING'
        | 'CLEAN'
        | 'EDITED'
        | 'UNDETERMINED';
      setEntryStatuses((prev) => ({ ...prev, [selectedEntryId]: status }));
    }
  }, [selectedEntryId, proofs, localFormData, calculateEntryStatus]);

  // Stable reference for offer letter proofs used by the auto-rule effect below.
  const offerLetterProofs = useMemo(
    () => proofs.filter((p) => (p.type as string) === OFFER_LETTER_TYPE && !p.is_automatic_review),
    [proofs]
  );

  // ITL-2769: auto-rule effect extracted to a hook so its behavior is
  // testable in isolation via renderHook (see useBlockedEditedAutoRule.test.ts).
  useBlockedEditedAutoRule({
    olvLockState,
    offerLetterProofs,
    setLocalFormData
  });

  // Safety net logging for zero-proofs detection (ITL-2297)
  useZeroProofsLogging({
    workflow: 'fraud_detection',
    entryId: selectedEntryId,
    isProofsSuccess,
    proofsLength: proofs.length,
    folderId: entry?.folder_id,
    assignedEntryId: assignedEntry?.id
  });

  useEffect(() => {
    Object.keys(entryProofCache).forEach((entryId) => {
      if (entryId !== selectedEntryId) {
        const cachedProofs = entryProofCache[entryId];
        const status = calculateEntryStatus(entryId, cachedProofs) as
          | 'PENDING'
          | 'CLEAN'
          | 'EDITED'
          | 'UNDETERMINED';
        setEntryStatuses((prev) => {
          if (prev[entryId] !== status) {
            return { ...prev, [entryId]: status };
          }
          return prev;
        });
      }
    });
  }, [localFormData, entryProofCache, selectedEntryId, calculateEntryStatus]);

  const checkAssignedEntryRuled = () => {
    if (!assignedEntry?.id) return false;

    // ITL-2816 (C2): fail closed when the selected entry's proofs have not loaded.
    // `isEntryRuled` returns true for an empty proof set, so without this guard a
    // fast tab-switch-then-submit (or the unresolved store-completeness root cause)
    // could pass the gate before the proofs arrive — strictly worse than the old
    // `completedEntries.has(...)` fallback, which returned false for an unvisited
    // entry. The render guards below also block on these states, but gating here
    // keeps the predicate safe regardless of call site.
    if (isLoadingProofs || proofs.length === 0) return false;

    // ITL-2816: gate on the SUBMITTED (selected) entry's proofs via the pure
    // `isEntryRuled` helper (`proofs` is scoped to the selected entry through
    // useProofsFilteredByEntryId(selectedEntryId)), regardless of whether it is
    // the assigned one. The prior `completedEntries.has(assignedEntry.id)`
    // fallthrough validated the ASSIGNED entry while submit targets the selected
    // one, letting a still-PENDING proof on a non-assigned entry pass the gate
    // (the multi-entry strand, PS-5964). OL proofs stay in the gating set
    // (ITL-2681 Phase 4 step 5): AI-pipeline success alone leaves proof.result
    // at :PENDING; only the OLV panel rules them.
    return isEntryRuled(proofs, localFormData);
  };

  if (!entry) {
    return <div>Loading...</div>;
  }

  if (isLoadingProofs) {
    return <div>Loading documents...</div>;
  }

  if (proofs.length === 0) {
    return <div>No documents available for review.</div>;
  }

  const offerLetterPanel = showOfferLetterPanel ? (
    <OfferLetterVerificationPanel
      currentProof={currentProof}
      onNextDocument={handleNextDocument}
      onReviewReport={handleReviewReport}
      isLastDocument={isLastDocument || !!currentProof?.is_automatic_review}
      onUpdateLocalFormData={setLocalFormData}
      lockState={olvLockState}
    />
  ) : null;

  return (
    <FraudTrendEvidenceProvider>
      <Container isMainSidebarOpen={isOpenSidebar}>
        {!(isInspectorPanelEnabled && (isDocumentViewerVisible || isPotentialMatchesVisible)) && (
          <LeftPanelDetails
            entry={entry}
            folder={folder || null}
            currentManualIndex={currentManualIndex}
            manualProofCount={manualProofCount}
            propertyData={getPropertyData || undefined}
            companyData={getCompanyData || undefined}
            onReviewReport={handleReviewReport}
            hasMatches={hasMatches}
            matchesCount={matches.length}
            showPotentialMatches={() => setMatchingEntriesScreenDismissed(false)}
            isSubmissionReviewMode={isSubmissionReviewMode}
          />
        )}

        {isPotentialMatchesVisible ? (
          <PotentialMatchesScreen
            matches={matches as IMatchingEntry[]}
            thisSubmission={entry}
            onDismiss={() => {
              setMatchingEntriesScreenDismissed(true);
              setUserDismissedMatches(true);
            }}
            onRefreshMatches={() => onGetMatchingEntriesByEntryId(selectedEntryId)}
          />
        ) : isSubmissionReviewMode ? (
          <SubmissionReviewPanel
            entries={entries || []}
            selectedEntryId={selectedEntryId}
            onEntryChange={setSelectedEntryId}
            localFormData={localFormData}
            onEditDocument={handleEditDocument}
            onSendReport={handleSendReport}
            proofs={proofs}
            assignedEntry={assignedEntry}
            entryStatuses={entryStatuses}
            checkAllEntriesRuled={checkAssignedEntryRuled}
            onReportPlatformFeedback={
              canReportPlatformFeedback ? () => setShowReportPlatformFeedbackModal(true) : undefined
            }
          />
        ) : isInspectorPanelEnabled ? (
          <InspectorPanel
            entry={entry}
            folder={folder || null}
            currentProof={currentProof}
            proofs={manuallyReviewableProofs}
            avRuledProofs={avRuledProofs}
            onProofChange={setSelectedProofId}
            isFirstDocument={isFirstDocument}
            isLastDocument={isLastDocument}
            canNavigateBack={canNavigateBack}
            canNavigateForward={canNavigateForward}
            onNextDocument={handleNextDocument}
            onPreviousDocument={handlePreviousDocument}
            onReviewReport={handleReviewReport}
            onReportPlatformFeedback={
              canReportPlatformFeedback ? () => setShowReportPlatformFeedbackModal(true) : undefined
            }
            localFormData={localFormData}
            onUpdateLocalFormData={setLocalFormData}
            hideResultControls={showOfferLetterPanel}
            onReclassifyToOfferLetter={handleReclassifyToOfferLetter}
            onReclassifyFromOfferLetter={handleReclassifyFromOfferLetter}
            onReclassifyCheckSetChange={handleReclassifyCheckSetChange}
            isPropertyOlvEnabled={propertyOlvEnabled}
            sidePanel={offerLetterPanel}
          />
        ) : (
          <DocumentViewer
            entry={entry}
            entriesInFolder={entries}
            setSelectedEntryId={setSelectedEntryId}
            folder={folder || null}
            proofs={proofs}
            selectedProofId={selectedProofId}
            onProofChange={setSelectedProofId}
            currentProof={currentProof}
            isFirstDocument={isFirstDocument}
            isLastDocument={isLastDocument}
            canNavigateBack={canNavigateBack}
            canNavigateForward={canNavigateForward}
            onNextDocument={handleNextDocument}
            onPreviousDocument={handlePreviousDocument}
            assignedEntry={assignedEntry}
            localFormData={localFormData}
            onUpdateLocalFormData={setLocalFormData}
            onReviewReport={handleReviewReport}
            onReportPlatformFeedback={
              canReportPlatformFeedback ? () => setShowReportPlatformFeedbackModal(true) : undefined
            }
            entryProofCache={entryProofCache}
            hideResultControls={showOfferLetterPanel}
            onReclassifyToOfferLetter={handleReclassifyToOfferLetter}
            onReclassifyFromOfferLetter={handleReclassifyFromOfferLetter}
            onReclassifyCheckSetChange={handleReclassifyCheckSetChange}
            isPropertyOlvEnabled={propertyOlvEnabled}
          />
        )}

        {!isInspectorPanelEnabled && offerLetterPanel}

        <SplitEntryModal />
        {canReportPlatformFeedback && (
          <ReportPlatformFeedbackModalContainer
            entryId={assignedEntry.id}
            currentProof={isSubmissionReviewMode ? undefined : currentProof}
            showModal={showReportPlatformFeedbackModal}
            setShowModal={setShowReportPlatformFeedbackModal}
          />
        )}
        <SendReportFraudModal
          text="Are you sure you want to continue?"
          onConfirm={handleConfirmSendReport}
        />
      </Container>
    </FraudTrendEvidenceProvider>
  );
};

export default ModernizedFraudAnalystReview;
